import React, { useEffect, useRef, useState } from 'react';
import './address-form.scss';
import { useServiceLocationStep } from '../../../../../hooks/owners-osb';
import { FMButton, FMDropdown, FMInput } from '@own/fds-react';
import { useOSBContentFragment } from '../../../../../hooks/owners-osb/aem-content-util/use-osb-content-fragment';
import DealerSearchDropdown from '../../dealer-search-dropdown/dealer-search-dropdown';
import GoogleMapService from '../../../../../services/osb-service/google-map-service/google-map-service';
import AppConfigurationService from '../../../../../services/app-configuration-service/app-configuration-service';
import CommentInput from '../../common/comment-input/comment-input';
import useUserProfile from '../../../../../hooks/use-user-profile';
import AddressFormSkeleton from '../address-form-skeleton/address-form-skeleton';
interface FormErrors {
    address1?: string;
    postalCode?: string;
    town?: string;
    parkingType?: string;
}

interface TouchedFields {
    address1?: boolean;
    postalCode?: boolean;
    town?: boolean;
    parkingType?: boolean;
}
interface AddressFormProps {
    onValidityChange: (isValid: boolean) => void;
}
const AddressForm: React.FC<AddressFormProps> = ({ onValidityChange }) => {
    const profile = useUserProfile();
    const hasPrepopulated = useRef(false);
    const appConfiguration = new AppConfigurationService().getAppConfiguration();
    const addressContent =
        useOSBContentFragment('common/osb-v3', 'pickup-and-delivery-address') ||
        {};
    const isLoading = !addressContent?.yourContactDetailsTitle;
    const parkingOptions = Array.isArray(addressContent?.parkingTypes)
        ? addressContent?.parkingTypes.map(parkingType => ({
              label: parkingType,
              value: parkingType,
          }))
        : [];
    const {
        osbServiceLocationDetail,
        setOSBServiceLocationStepPayload,
    } = useServiceLocationStep();
    const address1Ref = useRef<HTMLInputElement>(null);
    const [errors, setErrors] = useState<FormErrors>({});
    const [touched, setTouched] = useState<TouchedFields>({});

    const validate = (name: string, value: string): string | null => {
        switch (name) {
            case 'address1':
                if (!value)
                    return (
                        addressContent?.addressLine1RequiredFieldAlertMessage?.toString() ??
                        ''
                    );
                break;
            case 'postalCode':
                if (!value)
                    return (
                        addressContent?.postcodeIsRequiredFieldAlertMessage?.toString() ??
                        ''
                    );
                break;
            case 'town':
                if (!value)
                    return (
                        addressContent?.townOrCityIsRequiredFieldAlertMessage?.toString() ??
                        ''
                    );
                break;
            case 'parkingType':
                if (!value)
                    return (
                        addressContent?.parkingTypeIsRequiredFieldAlertMessage?.toString() ??
                        ''
                    );
                break;
            default:
                return null;
        }
        return null;
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setOSBServiceLocationStepPayload({ [name]: value });
        if (touched[name as keyof TouchedFields]) {
            const error = validate(name, value);
            setErrors(prev => ({ ...prev, [name]: error }));
        }
    };
    const handleCommentChange = (comment: string) => {
        setOSBServiceLocationStepPayload({ comment: comment });
    };
    const handlePostcodeChange = (postalCode: string) => {
        setOSBServiceLocationStepPayload({ postalCode: postalCode });
        if (postalCode.length >= 2) {
            GoogleMapService.getCityFromPostalCode(
                postalCode,
                appConfiguration.countryCode,
                appConfiguration?.languageRegionCode?.split('-')[0]
            ).then(city => {
                if (city) {
                    console.log('City found:', city);
                    setOSBServiceLocationStepPayload({ town: city });
                } else {
                    console.log('City not found.');
                }
            });
        }
    };
    const handleBlur = (
        e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        const error = validate(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
    };
    const handleCustomBlur = (name: string, value: string) => {
        console.log('name, value', name, value);

        setTouched(prev => ({ ...prev, [name]: true }));
        const error = validate(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
    };
    const handleSearchDifferentAddress = (
        e: React.MouseEvent<HTMLAnchorElement>
    ) => {
        e.preventDefault();
        setOSBServiceLocationStepPayload({
            address1: '',
            address2: '',
            address3: '',
            postalCode: '',
            town: '',
            parkingType: '',
            comment: '',
        });
        setErrors({});
        setTouched({});
        address1Ref.current?.focus();
    };
    useEffect(() => {
        if (profile?.profile && !hasPrepopulated.current) {
            const prefillPayload = {
                address1: profile.profile.address1,
                postalCode: profile.profile.zip,
                town: profile.profile.city,
            };
            setOSBServiceLocationStepPayload(prefillPayload);
            hasPrepopulated.current = true;
        }
    }, [profile]);
    useEffect(() => {
        const {
            address1,
            postalCode,
            town,
            parkingType,
        } = osbServiceLocationDetail;
        const allFieldsFilled = address1 && postalCode && town && parkingType;

        const hasNoErrors =
            !errors.address1 &&
            !errors.postalCode &&
            !errors.town &&
            !errors.parkingType;

        const isFormValid = !!allFieldsFilled && hasNoErrors;

        onValidityChange(isFormValid);
    }, [osbServiceLocationDetail, errors, onValidityChange]);
    if (isLoading) {
        return <AddressFormSkeleton />;
    }
    return (
        <div className="osb-v3-address-form-container">
            <h1>{addressContent?.yourContactDetailsTitle}</h1>

            <form>
                <div className="osb-v3-form-group">
                    <label htmlFor="addressLine1">
                        {addressContent?.addressLine1Label}
                    </label>
                    <FMInput
                        ref={address1Ref}
                        type="text"
                        id="address1"
                        name="address1"
                        value={osbServiceLocationDetail.address1 || ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        state={
                            touched.address1 && errors.address1
                                ? 'error'
                                : 'standard'
                        }
                    />
                    {touched.address1 && errors.address1 && (
                        <p className="osb-v3-error-message">
                            {errors.address1}
                        </p>
                    )}
                </div>

                <div className="osb-v3-form-group">
                    <label htmlFor="addressLine2">
                        {addressContent?.addressLine2Label}
                    </label>
                    <FMInput
                        type="text"
                        id="address2"
                        name="address2"
                        value={osbServiceLocationDetail.address2 || ''}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="osb-v3-form-group">
                    <label htmlFor="addressLine3">
                        {addressContent?.addressLine3Label}
                    </label>
                    <FMInput
                        type="text"
                        id="address3"
                        name="address3"
                        value={osbServiceLocationDetail.address3 || ''}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="osb-v3-form-row">
                    <div className="osb-v3-form-group osb-v3-postcode-group">
                        <label htmlFor="postcode">
                            {addressContent?.postcodeLabel}
                        </label>
                        <DealerSearchDropdown
                            dataTestId="postcode-input"
                            onSelect={(selectedValue: string): void => {
                                handlePostcodeChange(selectedValue);
                            }}
                            selectedValue={osbServiceLocationDetail.postalCode}
                            showFilter={false}
                            hasPostcodeSearch={true}
                            onInputChange={(inputValue: string): void => {
                                handleCustomBlur('postalCode', inputValue);
                            }}
                        />
                        {touched.postalCode && errors.postalCode && (
                            <p className="osb-v3-error-message">
                                {errors.postalCode}
                            </p>
                        )}
                    </div>
                    <div className="osb-v3-form-group">
                        <label htmlFor="townOrCity">
                            {addressContent?.townOrCityLabel}
                        </label>
                        <FMInput
                            type="text"
                            id="town"
                            name="town"
                            value={osbServiceLocationDetail.town || ''}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            state={
                                touched.town && errors.town
                                    ? 'error'
                                    : 'standard'
                            }
                        />
                        {touched.town && errors.town && (
                            <p className="osb-v3-error-message">
                                {errors.town}
                            </p>
                        )}
                    </div>
                </div>

                <div className="osb-v3-form-group">
                    <label htmlFor="parkingType">
                        {addressContent?.parkingTypeLabel}
                    </label>
                    <div className="osb-v3-select-wrapper osb-v3-address-form__dropdown-fix">
                        <FMDropdown
                            dataTestId="parking-type-dropdown"
                            id="parkingType"
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            options={parkingOptions}
                            value={osbServiceLocationDetail.parkingType}
                            state={
                                touched.parkingType && errors.parkingType
                                    ? 'error'
                                    : 'standard'
                            }
                            ariaLabel="Select your parking type"
                            name="parkingType"
                        />
                    </div>
                    {touched.parkingType && errors.parkingType && (
                        <p className="osb-v3-error-message">
                            {errors.parkingType}
                        </p>
                    )}
                </div>

                <div className="osb-v3-form-group">
                    <label htmlFor="comments">
                        {addressContent?.leaveACommentLabel}
                    </label>
                    <CommentInput
                        maxChars={500}
                        boxHeight={100}
                        charsRemainingText="Characters remaining"
                        initialComment={osbServiceLocationDetail.comment}
                        onCommentChange={handleCommentChange}
                    />
                </div>
            </form>
            <div className="osb-v3-form-group">
                <FMButton
                    className="address-form__search-link-btn"
                    ariaLabel={
                        addressContent?.searchForADifferentAddressLinkLabel as string
                    }
                    data-testid="search-link-button"
                    label={addressContent?.searchForADifferentAddressLinkLabel}
                    theme="light"
                    type="tertiary"
                    onClick={handleSearchDifferentAddress}
                    htmlButtonType="button"
                />
            </div>
        </div>
    );
};

export default AddressForm;
